"use client";

import { Badge } from "@/components/radix/badge";
import { BreadcrumbItem } from "@/utils/breadcrumb-utils";
import { Home, MapPin } from "lucide-react";
import Link from "next/link";
import React from "react";

interface BreadcrumbComponentProps {
	item: BreadcrumbItem;
	isMobile: boolean;
	isDisabled?: boolean;
}

interface ClickableBreadcrumbProps extends BreadcrumbComponentProps {
	onClick: () => void;
	disabled?: boolean;
}

/**
 * Get icon for breadcrumb variant
 */
export const getIcon = (variant: string) => {
	switch (variant) {
		case "city":
			return <MapPin className="w-3 h-3 mr-1" />;
		case "default":
			return <Home className="w-3 h-3 mr-1" />;
		default:
			return null;
	}
};

/**
 * Get badge className based on mobile state, clickability, disabled and loading states
 */
export const getBadgeClassName = (isMobile: boolean, isClickable: boolean, isDisabled?: boolean) => {
	const baseClasses = "flex items-center transition-colors";

	if (isDisabled) {
		// Disabled state: muted colors, no hover effects, no cursor pointer
		const disabledClasses = "text-muted-foreground cursor-default opacity-60";
		return `${baseClasses} ${disabledClasses} ${isMobile ? "px-1 py-0.5 text-xs max-w-none" : "max-w-[100px]"}`;
	}

	if (isMobile) {
		// Mobile: no outline, minimal padding, compact design
		return `${baseClasses} ${isClickable ? "cursor-pointer hover:text-primary-700" : ""} px-1 py-0.5 text-xs max-w-none`;
	} else {
		// Desktop: keep current styling with outline
		return `${baseClasses} ${isClickable ? "cursor-pointer hover:bg-primary/10 hover:text-primary-700" : ""} max-w-[100px]`;
	}
};

/**
 * Render ellipsis breadcrumb
 */
export const EllipsisBreadcrumb: React.FC<{ isMobile: boolean }> = ({ isMobile }) => {
	if (isMobile) {
		return <span className="text-xs text-muted-foreground px-1">...</span>;
	}
	return (
		<Badge variant={"outline"} className="cursor-default">
			...
		</Badge>
	);
};

/**
 * Render navigation breadcrumb (Home)
 */
export const NavigationBreadcrumb: React.FC<BreadcrumbComponentProps> = ({ item, isMobile }) => {
	if (!("href" in item) || !item.href) return null;

	return (
		<Link href={item.href} className="hover:text-primary-700 transition-colors">
			{isMobile ? (
				<span className={getBadgeClassName(isMobile, true)}>
					{getIcon(item.variant || "default")}
					<span className="truncate">{item.label}</span>
				</span>
			) : (
				<Badge
					variant={"outline"}
					className="hover:bg-primary/10 transition-colors cursor-pointer max-w-[100px] flex items-center"
				>
					{getIcon(item.variant || "default")}
					<span className="truncate">{item.label}</span>
				</Badge>
			)}
		</Link>
	);
};

/**
 * Render clickable breadcrumb with custom handler
 */
export const ClickableBreadcrumb: React.FC<ClickableBreadcrumbProps> = ({
	item,
	isMobile,
	onClick,
	disabled = false,
}) => {
	const isInteractionDisabled = disabled;

	if (isMobile) {
		return (
			<span
				className={getBadgeClassName(isMobile, !isInteractionDisabled, isInteractionDisabled)}
				onClick={isInteractionDisabled ? undefined : onClick}
			>
				{getIcon(item.variant || "default")}
				<span className="truncate">{item.label}</span>
			</span>
		);
	}

	return (
		<Badge
			variant={"outline"}
			className={`${
				isInteractionDisabled
					? "text-muted-foreground cursor-default opacity-60 max-w-[100px] flex items-center"
					: "hover:bg-primary/10 hover:text-primary-700 transition-colors cursor-pointer max-w-[100px] flex items-center"
			}`}
			onClick={isInteractionDisabled ? undefined : onClick}
		>
			{getIcon(item.variant || "default")}
			<span className="truncate">{item.label}</span>
		</Badge>
	);
};

/**
 * Render static breadcrumb (non-clickable)
 */
export const StaticBreadcrumb: React.FC<BreadcrumbComponentProps> = ({ item, isMobile, isDisabled }) => {
	if (isMobile) {
		return (
			<span className={getBadgeClassName(isMobile, false, isDisabled)}>
				{getIcon(item.variant || "default")}
				<span className="truncate">{item.label}</span>
			</span>
		);
	}

	return (
		<Badge
			variant={"outline"}
			className={`max-w-[100px] flex items-center ${isDisabled ? "text-muted-foreground opacity-60" : ""}`}
		>
			{getIcon(item.variant || "default")}
			<span className="truncate">{item.label}</span>
		</Badge>
	);
};
